import React, { useRef } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';


const StyledBox = styled(Box)(({ theme }) => ({
    position: 'sticky',
    top: 0,
    zIndex: 50,
    backgroundColor: theme.palette.background.default,
}));

const CategoryScrollContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    overflowX: 'auto',
    overflowY: 'hidden', // Prevent vertical scrolling
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    margin: '0 -4px',
    [theme.breakpoints.up('sm')]: {
        margin: '0 -8px',
    },
}));

const CategoryBox = styled(Box)(({ theme, selected }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(1, 1),
    cursor: 'pointer',
    borderBottom: selected
        ? `2px solid ${theme.palette.text.primary}`
        : '2px solid transparent',
    transition: 'border-bottom 0.2s ease, transform 0.2s ease',
    backgroundColor: 'transparent',
    width: '80px',
    margin: '0 6px',
    opacity: 0,
    transform: 'translateY(10px)',
    animation: 'fadeInUp 0.4s ease forwards',
    '&:hover': {
        borderBottom: `2px solid ${theme.palette.divider}`,
        transform: 'translateY(-2px)',
    },
    '& .MuiTypography-root': {
        fontSize: '0.75rem',
        lineHeight: 1.1,
        textAlign: 'center',
        marginTop: '4px',
    },
}));


const CategoryIcon = styled('img')({
    width: '42px',
    height: '42px',
    objectFit: 'contain',
    borderRadius: '6px',
    transition: 'transform 0.3s ease',
    marginBottom: 0,
});



const ListingsHeader = ({ categories, selectedCategory, onCategorySelect }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Fallback icon now points to the local X.png in the icons folder
    const fallbackIcon = `${process.env.PUBLIC_URL}/icons/X.png`;

    // Create a ref for the scrollable container
    const scrollRef = useRef(null);

    // Functions to scroll the container left or right by 500 pixels
    const scrollLeft = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: -500, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 500, behavior: 'smooth' });
        }
    };

    return (
        <StyledBox>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box position="relative" width="100%">
                    {/* Left Arrow */}
                    <IconButton
                        onClick={scrollLeft}
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                        }}
                    >
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    {/* Scrollable Categories */}
                    <CategoryScrollContainer ref={scrollRef}>
                        {categories.map(category => {
                            const isSelected = selectedCategory?.id === category.id;
                            const displayName =
                                category.short_name || category.name.split(' ')[0];
                            const fullName = category.name;

                            return (
                                <Tooltip
                                    key={category.id}
                                    title={fullName}
                                    arrow
                                    placement="bottom"
                                    enterDelay={300}
                                >
                                    <CategoryBox
                                        selected={isSelected}
                                        onClick={() => onCategorySelect(category)}
                                    >
                                        <CategoryIcon
                                            selected={isSelected}
                                            src={`${process.env.PUBLIC_URL}/icons/${category.id}.png`}
                                            alt={fullName}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = fallbackIcon;
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            style={{
                                                marginBottom: -8, // Negative margin to pull the text closer to border
                                                paddingBottom: 0,
                                                fontSize: isMobile ? 13 : 15,
                                                lineHeight: 1,
                                                fontFamily: "MavenPro, sans-serif",
                                                fontWeight: 500,
                                            }}
                                        >
                                            {displayName}
                                        </Typography>
                                    </CategoryBox>
                                </Tooltip>
                            );
                        })}
                    </CategoryScrollContainer>
                    {/* Right Arrow */}
                    <IconButton
                        onClick={scrollRight}
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                        }}
                    >
                        <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        </StyledBox>
    );
};

export default ListingsHeader;
